import {Injectable} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {defer, filter, merge, Observable, of, Subject} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {dependOn} from '../../../common/rxjs/depend-on';
import {Deliverable, DeliverableId} from '../dex-streaming/types/deliverable';
import {DeliverableBalance} from './deliverable-balance';
import {Erc20BalancesService} from './erc20-balances.service';

interface BalanceResolvedEvent {
    accountAddress: string;
    dBalance: DeliverableBalance;
}

const BALANCE_REQ_TIMEOUT_MS = 300;

interface BalanceReqRec {
    promise: Promise<DeliverableBalance>;
    ts?: number;
}

const setDoubleMapItem = <K1, K2, V>(map: Map<K1, Map<K2, V>>, k1: K1, k2: K2, v: V): void => {
    let map1 = map.get(k1);

    if (!map1) {
        map1 = new Map<K2, V>();
        map.set(k1, map1);
    }

    map1.set(k2, v);
};

@Injectable()
export class Erc20BalancesSharedService extends Erc20BalancesService {

    private readonly balancesByAccount = new Map<string, Map<DeliverableId, DeliverableBalance>>();

    private readonly balanceResolved$ = new Subject<BalanceResolvedEvent>();

    private readonly balancesReqByAccount = new Map<string, Map<DeliverableId, BalanceReqRec>>();

    private readonly _authSub = this.dynamicZyxDaemonService.getLogOutStream().pipe(
        tap(() => this.cleanup()),
        takeUntilDestroyed()
    ).subscribe();

    public getDelBalanceStream(accountAddress: string, d: Deliverable): Observable<DeliverableBalance> {
        return this.getPassiveDelBalanceStream(accountAddress, d).pipe(
            dependOn(this.fetchDeliverableBalance(accountAddress, d))
        );
    }

    public getPassiveDelBalanceStream(accountAddress: string, d: Deliverable): Observable<DeliverableBalance> {
        return defer(() => {
            const delMap = this.balancesByAccount.get(accountAddress);
            const isKnown = delMap?.has(d.deliverableId);

            const balanceStream$ = this.balanceResolved$.pipe(
                filter(e =>
                    e.accountAddress === accountAddress
                    && e.dBalance.deliverable.deliverableId === d.deliverableId
                ),
                map(e => e.dBalance)
            );

            return isKnown
                ? merge(of(delMap.get(d.deliverableId)), balanceStream$)
                : balanceStream$;
        });
    }

    protected override async readDeliverableBalance(
        accountAddress: string,
        d: Deliverable
    ): Promise<DeliverableBalance> {
        let balanceReq = this.balancesReqByAccount.get(accountAddress)?.get(d.deliverableId);
        let isReqCreator = false;

        if (balanceReq?.ts && balanceReq.ts + BALANCE_REQ_TIMEOUT_MS <= Date.now()) {

            // Setting to undefined should be faster, than deleting.
            setDoubleMapItem(this.balancesReqByAccount, accountAddress, d.deliverableId, undefined);
            balanceReq = undefined;
        }

        if (!balanceReq) {
            const promise = super.readDeliverableBalance(accountAddress, d);
            balanceReq = {promise};
            isReqCreator = true;

            setDoubleMapItem(this.balancesReqByAccount, accountAddress, d.deliverableId, balanceReq);
        }

        const balance = await balanceReq.promise;

        if (isReqCreator) {
            balanceReq.ts = Date.now();
            this.setDelBalance(accountAddress, balance);
        }

        return balance;
    }

    private setDelBalance(accountAddress: string, dBalance: DeliverableBalance): void {
        let delMap = this.balancesByAccount.get(accountAddress);

        if (!delMap) {
            delMap = new Map<DeliverableId, DeliverableBalance>();
            this.balancesByAccount.set(accountAddress, delMap);
        }

        delMap.set(dBalance.deliverable.deliverableId, dBalance);

        this.balanceResolved$.next({accountAddress, dBalance});
    }

    private cleanup(): void {
        this.balancesByAccount.clear();
        this.balancesReqByAccount.clear();
    }

}
